import { useCallback, useState } from 'react';
import { getFigmaPreviewUrl } from '../../../../services/taskServices';
import { captureException } from '../../../../services/logService';
import { updateDiscussionMessageDetail } from '../../../../services/chatMessageService';
import { MessageDetailsInterface } from '../../../../interfaces/chatMessageInterface';

interface FigmaUrlData {
  fileId: string;
  nodes: string | null;
}

interface UseFigmaRefreshReturn {
  figmaRefreshLoading: string | null;
  onClickHandleRefreshFigma: (item: MessageDetailsInterface, figmaURL: string) => Promise<void>;
}

interface UseFigmaRefreshParams {
  updateMessageInState: (updatedHtmlString: string, messageId: string) => void;
}

const getMessageBody = (messageHtml: string): HTMLBodyElement | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(messageHtml, 'text/html');
  return doc.querySelector('body');
};

const toggleRefreshAnimation = (body: HTMLElement, isSpin: boolean): void => {
  const refreshIcon = body.querySelector('.refresh-icon');
  if (refreshIcon) {
    refreshIcon.classList.toggle('refresh-icon-spin', isSpin);
  }
};

export const useFigmaRefresh = ({ updateMessageInState }: UseFigmaRefreshParams): UseFigmaRefreshReturn => {
  const [figmaRefreshLoading, setFigmaRefreshLoading] = useState<string | null>(null);

  const extractFigmaUrlData = useCallback((figmaUrl: string): FigmaUrlData => {
    const url = new URL(figmaUrl);
    return { fileId: url.pathname?.split('/')[2] || '', nodes: url.searchParams.get('node-id') };
  }, []);

  const onClickHandleRefreshFigma = useCallback(
    async (item: MessageDetailsInterface, figmaURL: string) => {
      try {
        setFigmaRefreshLoading(item?.id);
        const body = getMessageBody(item?.message || '');
        if (!body) return;

        const { fileId, nodes } = extractFigmaUrlData(figmaURL);
        const figmaData = await getFigmaPreviewUrl({ fileId, nodes });
        if (!figmaData) return;

        const containers = Array.from(body.querySelectorAll('.figma-main-div')) as HTMLElement[];
        const container = containers.find((element) => element.getAttribute('url') === figmaURL) || null;
        const previewImage = container?.querySelector('.figma-preview-image') as HTMLImageElement | null;

        if (previewImage) {
          previewImage.src = figmaData?.filePreviewUrl || '';
        }
        if (container) {
          container.setAttribute('filepreviewurl', figmaData?.filePreviewUrl || '');
          container.setAttribute('framenames', figmaData?.frameNames || '');
          container.setAttribute('projectname', figmaData?.projectName || '');
        }

        const description = container?.querySelector('.figma-description');
        if (description) {
          description.textContent = figmaData?.frameNames;
        }

        toggleRefreshAnimation(body, false);

        const filename = container?.querySelector('.figma-filename');
        if (filename) {
          filename.textContent = figmaData?.projectName || '';
        }

        const updatedHtmlString = body.innerHTML;
        updateMessageInState(updatedHtmlString, item.id);
        await updateDiscussionMessageDetail(item?.id, { Message: updatedHtmlString });
      } catch (error) {
        captureException(error);
      } finally {
        setFigmaRefreshLoading(null);
      }
    },
    [extractFigmaUrlData, updateMessageInState]
  );

  return {
    figmaRefreshLoading,
    onClickHandleRefreshFigma
  };
};
